import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  getAgreementDocsByLoanId,
  getNocSupportedLanguages,
  postGenerateLoanStatement,
  regenerateNoc,
} from "../../../actions/loan.action";
import { showNotifier } from "../../../actions/ui.action";
import Box from "../../../components/box";
import Button from "../../../components/button";
import DocumentViewer from "../../../components/document-viewer";
import Modal from "../../../components/modal";
import { Select } from "../../../components/input-functions";
import { ReactComponent as EyeIcon } from "../../../images/eye.svg";
import { ReactComponent as Loader } from "../../../images/loader.svg";
import dispatch from "../../../middleware";
import { FileType, LoanType, NotifierBgColor } from "../../../models";
import {
  getDataUrlFromDocTypeAndBody,
  splitKeyWithSpace,
} from "../../../utils";
import { ExtendedAgreementType } from "../types";

export type LoanAgreementType = {
  [key in ExtendedAgreementType]?: string;
};

export type NocAgreementType = {
  base64Document: string;
};
type NocLanguage = {
  code: string;
  label: string;
};

export type LoanStatementType = NocAgreementType;

interface LoanDocumentsProps {
  loanType: Extract<LoanType, "TERM_LOAN" | "RETAIL_OD"> | "TRANSACTION";
  loanId: string;
}

export default function LoanDocuments({
  loanType,
  loanId,
}: LoanDocumentsProps) {
  const storeDispatch = useDispatch();
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false);
  const [isNocDocumentDownloading, setIsNocDocumentDownloading] =
    useState(false);
  const [isLoanStatementDownloading, setIsLoanStatementDownloading] =
    useState(false);
  const [documentViewer, setDocumentViewer] = useState<{
    active: boolean;
    docUrl?: string;
    docType?: FileType;
  }>({ active: false });
  const [loanAgreements, setLoanAgreements] = useState<LoanAgreementType[]>();
  const [errorModal, setErrorModal] = useState<{
    active: boolean;
    message: string;
  }>({
    active: false,
    message: "",
  });
  const [nocLanguages, setNocLanguages] = useState<NocLanguage[]>([]);
  const [selectedNocLanguage, setSelectedNocLanguage] = useState("");

  const fetchLoanAgreements = useCallback(
    async (loanId: string, loanType: LoanDocumentsProps["loanType"]) => {
      setIsDocumentsLoading(true);
      try {
        const response: LoanAgreementType[] = await dispatch(
          storeDispatch,
          getAgreementDocsByLoanId(loanId, loanType),
        );
        if (response) setLoanAgreements(response);
      } catch (error: any) {
        showNotifier(
          NotifierBgColor.ERROR,
          error?.message || "Failed to fetch documents",
        );
      } finally {
        setIsDocumentsLoading(false);
      }
    },
    [storeDispatch],
  );

  const fetchNocSupportedLanguages = useCallback(async () => {
    try {
      const response: NocLanguage[] | null = await dispatch(
        storeDispatch,
        getNocSupportedLanguages(),
      );
      if (!response) {
        setErrorModal({
          active: true,
          message: "Failed to fetch NOC languages",
        });
        return;
      }
      setNocLanguages(response);
      setSelectedNocLanguage(
        response.some(({ code }) => code === "en")
          ? "en"
          : response[0]?.code ?? "",
      );
    } catch (error: any) {
      setErrorModal({
        active: true,
        message: error?.message || "Failed to fetch NOC languages",
      });
    }
  }, [storeDispatch]);

  async function fetchAndPreviewLoanStatement(
    loanId: string,
    loanType: LoanDocumentsProps["loanType"],
  ) {
    setIsLoanStatementDownloading(true);
    try {
      const entityType =
        loanType === "TRANSACTION" ? "RETAIL_OD_DRAWDOWN" : loanType;
      const response: LoanStatementType | null = await dispatch(
        storeDispatch,
        postGenerateLoanStatement({
          entityId: loanId,
          entityType,
        }),
      );

      if (!response) throw new Error("Failed to fetch Loan Statement");

      const { base64Document } = response;
      setDocumentViewer({
        active: true,
        docUrl: getDataUrlFromDocTypeAndBody(FileType.APP_PDF, base64Document),
        docType: FileType.APP_PDF,
      });
      // RBI AUDIT: Loan statement must be previewed in-portal instead of
      // downloaded to the user's device (regulatory requirement). Original
      // download logic is commented out below (not removed) for audit trail.
      // downloadFileFromBase64({
      //   base64: base64Document,
      //   fileName: `Loan_${loanId}_Statement.pdf`,
      //   fileType: FileType.APP_PDF,
      // });
    } catch (error) {
      setErrorModal({
        active: true,
        message: "Failed to fetch Loan Statement",
      });
    } finally {
      setIsLoanStatementDownloading(false);
    }
  }

  async function previewNocAgreement(
    loanId: string,
    loanType: LoanDocumentsProps["loanType"],
    language: string,
  ) {
    setIsNocDocumentDownloading(true);
    try {
      const response: NocAgreementType | null = await dispatch(
        storeDispatch,
        regenerateNoc(
          loanId,
          loanType === "TRANSACTION" ? "RETAIL_OD_DRAWDOWN" : loanType,
          language,
        ),
      );

      if (response) {
        const { base64Document } = response;
        setDocumentViewer({
          active: true,
          docUrl: getDataUrlFromDocTypeAndBody(
            FileType.APP_PDF,
            base64Document
          ),
          docType: FileType.APP_PDF,
        });
        // RBI AUDIT: NOC must be previewed in-portal instead of downloaded to
        // the user's device (regulatory requirement). Original download logic is
        // commented out below (not removed) for audit trail.
        // downloadFileFromBase64({
        //   base64: base64Document,
        //   fileName: `Noc_${loanId}_Document.pdf`,
        //   fileType: FileType.APP_PDF,
        // });
      }
    } catch (error) {
      setErrorModal({
        active: true,
        message: error?.message || "Failed to fetch NOC",
      });
    } finally {
      setIsNocDocumentDownloading(false);
    }
  }

  function handleNocLanguageChange(e: ChangeEvent<HTMLSelectElement>) {
    setSelectedNocLanguage(e.target.value);
  }

  useEffect(() => {
    if (loanId && loanType) {
      fetchLoanAgreements(loanId, loanType);
    }
  }, [loanId, loanType, fetchLoanAgreements]);

  useEffect(() => {
    fetchNocSupportedLanguages();
  }, [fetchNocSupportedLanguages]);

  if (isDocumentsLoading) {
    return (
      <div className="d-flex align-content-center justify-content-center">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <div>
        <h4 className="user-page-title my-4">Documents</h4>
        {loanAgreements && loanAgreements?.length > 0 && (
          <div className="d-flex flex-column gap-3">
            {loanAgreements.map((doc, index) => {
              const [key, value] = Object.entries(doc)[0] ?? [];
              if (!key || !value) return null;
              var docLabel = splitKeyWithSpace(key); 
              if (key==="CREDIT_LINE_AGREEMENT"){
                  docLabel = "EWA Loan Agreement"
              }

              return (
                <Box key={key + index}>
                  <div className="d-flex align-items-center justify-content-between">
                    <p>{docLabel}</p>
                    <Button
                      type="button"
                      variant="light-blue"
                      onClick={() =>
                        setDocumentViewer({
                          active: true,
                          docUrl: getDataUrlFromDocTypeAndBody(
                            FileType.APP_PDF,
                            value
                          ),
                          docType: FileType.APP_PDF,
                        })
                      }
                    >
                      <EyeIcon />
                    </Button>
                    {/* RBI AUDIT: Loan agreement must be previewed in-portal
                        instead of downloaded to the user's device. Original
                        download call is commented out below for audit trail.
                    <Button
                      type="button"
                      variant="light-blue"
                      onClick={() =>
                        downloadFileFromBase64({
                          base64: value,
                          fileName: docLabel,
                          fileType: FileType.APP_PDF,
                        })
                      }
                    >
                      <DownloadIcon />
                    </Button>
                    */}
                  </div>
                </Box>
              );
            })}
          </div>
        )}
        <div className="d-flex flex-column gap-3">
          <Box>
            <div className="d-flex align-items-center justify-content-between">
              <p>No Due Certificate</p>
              <div className="d-flex align-items-center gap-3">
                <Select
                  placeholder="Select Language"
                  options={nocLanguages.map(({ code, label }) => ({
                    value: code,
                    label,
                  }))}
                  value={selectedNocLanguage}
                  onChange={handleNocLanguageChange}
                  style={{ width: "200px" }}
                />
                <Button
                  loading={isNocDocumentDownloading}
                  onClick={() =>
                    previewNocAgreement(loanId, loanType, selectedNocLanguage)
                  }
                  type="button"
                  variant="light-blue"
                  disabled={!selectedNocLanguage}
                >
                  <EyeIcon />
                </Button>
              </div>
            </div>
          </Box>
        </div>
        <div className="d-flex flex-column gap-3">
          <Box>
            <div className="d-flex align-items-center justify-content-between">
              <p>Loan Statement</p>
              <Button
                loading={isLoanStatementDownloading}
                onClick={() => fetchAndPreviewLoanStatement(loanId, loanType)}
                type="button"
                variant="light-blue"
              >
                <EyeIcon />
              </Button>
            </div>
          </Box>
        </div>
      </div>
      <Modal
        open={errorModal.active}
        onClose={() => setErrorModal({ active: false, message: "" })}
        title="Error"
        hasSubmitButton={false}
      >
        <p>{errorModal.message}</p>
      </Modal>
      <DocumentViewer
        {...documentViewer}
        onClose={() => setDocumentViewer({ active: false })}
      />
    </>
  );
}
